import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { UpdateBooking } from '@/api/data-contracts';

dayjs.extend(utc);

/** Normalizes a date-only range to the API's expected day-bounds timestamps; values that
 *  already carry a time component pass through unchanged. Shared by the bookings and
 *  media-plans features (a feature must not import another feature's internals). */
export const normalizeAvailabilityRange = (
  start: string,
  end: string,
): NonNullable<UpdateBooking['availability']> => ({
  start: start.includes('T') ? start : `${start}T00:00:00Z`,
  end: end.includes('T') ? end : `${end}T20:59:59Z`,
});

/**
 * The calendar day an availability END means — the inverse of the decoration above.
 *
 * Ends come back as instants authored in several shapes: `20:59:59Z` (this app's Bucharest
 * end-of-day) and `23:59:59Z` (the backend's UTC canon) both mark the same last booked day. Reading
 * them in the browser's zone only agrees with the backend for the first: anywhere east of UTC the
 * canonical one falls on the following day, so it must be resolved against UTC, not locally.
 *
 * Mirrors the backend rule exactly: a UTC time past midnight is inclusive of its own UTC date,
 * exactly `00:00:00Z` is exclusive of it. Values that are already bare days pass through.
 */
export const availabilityEndDay = (end: string): string => {
  if (!end.includes('T')) return end;

  const instant = dayjs.utc(end);
  if (!instant.isValid()) return end;

  const isExclusiveBound = instant.isSame(instant.startOf('day'));
  return (isExclusiveBound ? instant.subtract(1, 'day') : instant).format('YYYY-MM-DD');
};

/**
 * The calendar day an availability START means — the start-side twin of the above.
 *
 * Starts are authored as `00:00:00Z`, by the decoration at the top of this file and by the backend
 * alike, so the day one means is simply its UTC date; there is no exclusive-bound case to undo,
 * because midnight is where a start belongs. Reading it in the browser's zone agrees from UTC
 * eastward and lands a day early everywhere west — the same defect as the end-side one, pointing
 * the other way, and just as invisible to anyone living on the agreeing side of it.
 *
 * Values that are already bare days pass through.
 */
export const availabilityStartDay = (start: string): string => {
  if (!start.includes('T')) return start;

  const instant = dayjs.utc(start);
  return instant.isValid() ? instant.format('YYYY-MM-DD') : start;
};

const NEXT_DAY_COHORT_START_HOUR_UTC = 21;

/**
 * The calendar day a RANGE start means, per the backend's `collapseAsRangeStart`: instants in
 * `[21:00Z, 24:00Z)` are the local-midnight cohort and mean the NEXT UTC day.
 * Unlike {@link availabilityStartDay} (which reads canonical `00:00:00Z` booking starts), this
 * also accepts the backend's canon `23:59:59Z` end shape reused as a start — that instant sits in
 * the cohort and begins the following day, which is how an extension window opening at the old
 * end's instant lands on the right day. The app's own `20:59:59Z` Bucharest end shape does NOT:
 * it collapses to its own day, exactly as the backend's `collapseAsRangeStart` would read it.
 */
export const availabilityRangeStartDay = (start: string): string => {
  if (!start.includes('T')) return start;

  const instant = dayjs.utc(start);
  if (!instant.isValid()) return start;

  const belongsToNextDay = instant.hour() >= NEXT_DAY_COHORT_START_HOUR_UTC;
  return (belongsToNextDay ? instant.add(1, 'day') : instant).format('YYYY-MM-DD');
};

/**
 * The window's length in months, as the backend defines a booking's `availability.period`
 * (`Availability.period()`): over every calendar month the window touches, occupied days ÷ that
 * month's length, summed on the INCLUSIVE day range the instants mean. A window whose collapsed
 * end lands before its start clamps to the single start day, mirroring the backend's degenerate
 * `DateRange` rule.
 */
export const availabilityPeriodInMonths = (start: string, end: string): number => {
  const from = dayjs.utc(availabilityRangeStartDay(start));
  const collapsedEnd = dayjs.utc(availabilityEndDay(end));
  if (!from.isValid() || !collapsedEnd.isValid()) return 0;

  const to = collapsedEnd.isBefore(from) ? from : collapsedEnd;

  let months = 0;
  for (let cursor = from.startOf('month'); !cursor.isAfter(to); cursor = cursor.add(1, 'month')) {
    const first = from.isAfter(cursor) ? from : cursor;
    const monthLastDay = cursor.date(cursor.daysInMonth());
    const last = to.isBefore(monthLastDay) ? to : monthLastDay;
    months += (last.diff(first, 'day') + 1) / cursor.daysInMonth();
  }
  return months;
};

/**
 * Whether the availability window sits inside `[range.start, range.end]`, compared on the
 * calendar days its instants mean — a raw string or local compare reads the backend's
 * `23:59:59Z` canon a day late east of UTC and an exclusive-midnight end a day late
 * everywhere. Range bounds are bare days (as the filter pickers produce); an open bound or a
 * missing window value constrains nothing, mirroring the grids' legacy filter semantics.
 */
export const isAvailabilityWithinDayRange = (
  window: { start?: string; end?: string },
  range: { start?: string; end?: string },
): boolean => {
  if (range.start && window.start && availabilityRangeStartDay(window.start) < range.start) {
    return false;
  }
  if (range.end && window.end && availabilityEndDay(window.end) > range.end) {
    return false;
  }
  return true;
};

/**
 * The window every one of [windows] allows, as bare days.
 *
 * A package is bookable only where the package window AND every member space's window agree, and a
 * member's shorter window blocks the package with no occupancy segment to show for it — so the
 * package rails split their gaps against this intersection, not the package window alone.
 *
 * Disjoint windows come back inverted (start after end): known-empty, which downstream draws as
 * "nothing inside", distinct from undefined — no complete window at all — which disables the
 * split. Incomplete windows are skipped rather than guessed at.
 */
export const intersectAvailabilityWindows = (
  windows: Array<{ start?: string; end?: string } | undefined>,
): { start: string; end: string } | undefined => {
  const complete = windows.filter(
    (window): window is { start: string; end: string } => !!window?.start && !!window?.end,
  );
  if (!complete.length) return undefined;

  const startDays = complete.map((window) => availabilityRangeStartDay(window.start));
  const endDays = complete.map((window) => availabilityEndDay(window.end));

  return {
    start: startDays.reduce((a, b) => (a > b ? a : b)),
    end: endDays.reduce((a, b) => (a < b ? a : b)),
  };
};
